import { useState, useEffect, useCallback } from "react";
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

interface FilterState {
  standard: string;
  courseName: string;
  subject: string;
  level: string;
  languageName: string;
  createdBy: string;
  fromDate: string;
  toDate: string;
}

const useTestQuestion = () => {
  const [testquestionData, setTestQuestionData] = useState<any[]>([]);
  const [selectedTestQuestionView, setSelectedTestQuestionView] = useState<
    string | null
  >(null);
  const [selectedTestQuestionId, setSelectedTestQuestionId] = useState<
    string | null
  >(null);

  // Filter dropdown data
  const [courses, setCourses] = useState<any[]>([]);
  const [languages, setLanguages] = useState<any[]>([]);
  const [subjectName, setSubjectName] = useState<any[]>([]);
  const [standard, setStandard] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  // Filtered dropdown data based on dependencies
  const [filteredCourses, setFilteredCourses] = useState<any[]>([]);
  const [filteredsubjects, setFilteredsubjects] = useState<any[]>([]);

  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    standard: "",
    courseName: "",
    subject: "",
    level: "",
    languageName: "",
    createdBy: "",
    fromDate: "",
    toDate: "",
  });

  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const [paginationInfo, setPaginationInfo] = useState<{
    hasNext: boolean;
    hasPrev: boolean;
    totalPages: number;
    currentPage: number;
    totalDocs: number;
  }>({
    hasNext: false,
    hasPrev: false,
    totalPages: 0,
    currentPage: 1,
    totalDocs: 0,
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);
  const navigate = useNavigate();

  // Fetch initial data
  useEffect(() => {
    fetchCoursesAndLanguages();
    fetchUsers();
  }, []);

  // Handle filter dependencies
  useEffect(() => {
    // Filter courses based on selected standard
    if (filters.standard) {
      const filtered = courses.filter(
        (course) => course.standard === filters.standard
      );
      setFilteredCourses(filtered);
    } else {
      setFilteredCourses([]);
    }

    // Filter subjects based on both standard and course
    if (filters.standard && filters.courseName) {
      const filtered = subjectName.filter(
        (subject) =>
          subject.standard === filters.standard &&
          subject.courseName === filters.courseName
      );
      setFilteredsubjects(filtered);
    } else {
      setFilteredsubjects([]);
    }
  }, [filters.standard, filters.courseName, courses, subjectName]);

  // Clear dependent filters when parent changes
  useEffect(() => {
    if (!filters.standard) {
      setFilters((prev) => ({ ...prev, courseName: "", subject: "" }));
    }
  }, [filters.standard]);

  useEffect(() => {
    if (!filters.courseName) {
      setFilters((prev) => ({ ...prev, subject: "" }));
    }
  }, [filters.courseName]);

  const fetchCoursesAndLanguages = async () => {
    try {
      setLoading(true);
      const [
        coursesResponse,
        languagesResponse,
        subjectResponse,
        standardResponse,
      ] = await Promise.all([
        ApiService({ method: "GET", endpoint: apiPaths.getCourse }),
        ApiService({ method: "GET", endpoint: apiPaths.getlanguage }),
        ApiService({ method: "GET", endpoint: apiPaths.getSubject }),
        ApiService({ method: "GET", endpoint: apiPaths.getStandard }),
      ]);

      console.log(coursesResponse);
      setCourses(coursesResponse);
      setLanguages(languagesResponse);
      setSubjectName(subjectResponse);
      setStandard(standardResponse);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      // Add your API endpoint for fetching users
      const usersResponse = await ApiService({
        method: "GET",
        endpoint: apiPaths.fetchMasterUsers,
      });

      setUsers(usersResponse.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      // Don't show error toast for users fetch as it's not critical
    }
  };

  // Debounced search function
  const debounce = (func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  };

  // Create debounced search function
  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      // Reset to page 1 when searching
      setPaginationInfo((prev) => ({ ...prev, currentPage: 1 }));
      fetchData(1, pageSize, searchValue, filters);
    }, 500), // 500ms delay
    [pageSize, filters]
  );

  // Handle search term changes
  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  // Fetch data with pagination and search
  useEffect(() => {
    if (!searchTerm) {
      fetchData(paginationInfo.currentPage, pageSize, "", filters);
    }
  }, [paginationInfo.currentPage, pageSize]);

  // Separate useEffect for selectedTestQuestionId changes
  useEffect(() => {
    if (!selectedTestQuestionId) {
      fetchData(paginationInfo.currentPage, pageSize, searchTerm, filters);
    }
  }, [selectedTestQuestionId]);

  const buildQueryParams = (
    page: number,
    limit: number,
    search: string,
    filterState: FilterState
  ) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());

    if (search.trim()) {
      params.append("search", encodeURIComponent(search.trim()));
    }

    // Add filter parameters with proper encoding
    Object.entries(filterState).forEach(([key, value]) => {
      if (value && value.trim()) {
        params.append(key, encodeURIComponent(value.trim()));
      }
    });

    return params.toString();
  };

  const buildRequestPayload = (
    page: number,
    limit: number,
    search: string,
    filterState: FilterState
  ) => {
    return {
      page,
      limit,
      search: search.trim(),
      ...filterState,
    };
  };

  const fetchData = async (
    page: number = 1,
    limit: number = 10,
    search: string = "",
    filterState: FilterState = filters
  ) => {
    setLoading(true);
    try {
      const queryParams = buildQueryParams(page, limit, search, filterState);
      const endpoint = `${apiPaths.question}?${queryParams}`;

      console.log("Fetching data with endpoint:", endpoint);
      console.log("Filter state being sent:", filterState);

      const result = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });

      console.log("TEST QUESTION : ", result.docs);

      // Set the questions data from docs array
      setTestQuestionData(result.docs || []);

      // Update pagination info from backend response
      setPaginationInfo({
        hasNext: result.hasNext,
        hasPrev: result.hasPrev,
        totalPages: result.totalPages,
        currentPage: result.currentPage,
        totalDocs: result.totalDocs,
      });
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.error("Error fetching questions");
    } finally {
      setLoading(false);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);

    // Update pagination info to page 1 AND fetch data in one go
    setPaginationInfo((prev) => ({
      ...prev,
      currentPage: 1,
    }));

    fetchData(1, newPageSize, searchTerm, filters);
  };

  const formatDateToISODateString = (date: Date): string => {
    return date.toISOString().split("T")[0];
  };

  const handleDateFilter = async (fromDate: Date, toDate: Date) => {
    const formattedFromDate = formatDateToISODateString(fromDate);
    const formattedToDate = formatDateToISODateString(toDate);

    const updatedFilters = {
      ...filters,
      fromDate: formattedFromDate,
      toDate: formattedToDate,
    };

    setFilters(updatedFilters);
    setPaginationInfo((prev) => ({ ...prev, currentPage: 1 }));
    fetchData(1, pageSize, searchTerm, updatedFilters);
  };

  // Filter handling functions
  const handleFilterChange = (filterKey: string, value: string) => {
    setFilters((prev) => ({ ...prev, [filterKey]: value }));
  };

  const handleApplyFilters = () => {
    setPaginationInfo((prev) => ({ ...prev, currentPage: 1 }));
    fetchData(1, pageSize, searchTerm, filters);
  };

  const handleClearFilters = () => {
    const clearedFilters: FilterState = {
      standard: "",
      courseName: "",
      subject: "",
      level: "",
      languageName: "",
      createdBy: "",
      fromDate: "",
      toDate: "",
    };

    setFilters(clearedFilters);
    setFilteredCourses([]);
    setFilteredsubjects([]);
    setPaginationInfo((prev) => ({ ...prev, currentPage: 1 }));
    fetchData(1, pageSize, searchTerm, clearedFilters);
  };

  const handleGeneratePdf = async () => {
    if (!filters.standard || !filters.courseName || !filters.subject) {
      toast.error("Please select Standard, Stream and Subject to generate PDF");
      return;
    }

    try {
      toast.loading("Generating PDF...", { id: "questions-generate-pdf" });
      setLoading(true);

      const payload = buildRequestPayload(1, pageSize, searchTerm, filters);

      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.questionGeneratePdf,
        data: payload,
      });

      if (response?.success && response?.pdfUrl) {
        const pdfUrl = response.pdfUrl;
        // Open a blank tab immediately to avoid popup blockers
        const previewWindow = window.open(pdfUrl, "_blank");

        // Load the PDF into the tab we already opened
        if (previewWindow) {
          previewWindow.location.href = pdfUrl;
        } else {
          // Fallback in case window couldn't be opened
          window.open(pdfUrl, "_blank");
        }

        toast.success("PDF generated successfully", {
          id: "questions-generate-pdf",
        });
      } else {
        toast.error("Failed to generate PDF", {
          id: "questions-generate-pdf",
        });
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Error generating PDF", {
        id: "questions-generate-pdf",
      });
    } finally {
      toast.dismiss("questions-generate-pdf");
      setLoading(false);
    }
  };

  const handleView = (id: string) => {
    setSelectedTestQuestionView(id);
  };

  const handleEdit = (id: string) => {
    setSelectedTestQuestionId(id);
  };

  const handleDelete = async (id: string) => {
    setLoading(true);
    try {
      await ApiService({
        method: "DELETE",
        endpoint: `${apiPaths.question}/${id}`,
      });

      // After successful deletion, refresh the current page data with current search and filters
      await fetchData(
        paginationInfo.currentPage,
        pageSize,
        searchTerm,
        filters
      );
      toast.success("TestQuestion deleted successfully");
    } catch (error) {
      console.error("Error deleting TestQuestion:", error);
      toast.error("Error deleting TestQuestion");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUp = async (id: string, currentStatus: boolean) => {
    setLoading(true);
    const newStatus = currentStatus;
    console.log(newStatus);
    try {
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updateTestQuestion}/${id}`,
        data: { isActive: newStatus },
      });

      // Update the specific item in current page data
      setTestQuestionData((prevData) =>
        prevData.map((testquestion) =>
          testquestion._id === id
            ? { ...testquestion, isActive: newStatus }
            : testquestion
        )
      );
    } catch (error) {
      console.error("Error updating testquestion status:", error);
      toast.error("Error updating testquestion status");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > paginationInfo.totalPages) {
      return;
    }

    // Update pagination state first
    setPaginationInfo((prev) => ({
      ...prev,
      currentPage: page,
    }));

    // Then fetch data with current search term and filters
    fetchData(page, pageSize, searchTerm, filters);
  };

  const handleAdd = () => {
    navigate("/add-test-question");
  };

  // Clear search function
  const handleClearSearch = () => {
    setSearchTerm("");
    setPaginationInfo((prev) => ({ ...prev, currentPage: 1 }));
    fetchData(1, pageSize, "", filters);
  };

  // Manual search trigger (if needed)
  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
    setPaginationInfo((prev) => ({ ...prev, currentPage: 1 }));
    fetchData(1, pageSize, searchValue, filters);
  };

  // Checkbox selection handlers
  const handleSelectQuestion = (questionId: string) => {
    setSelectedQuestionIds((prev) => {
      if (prev.includes(questionId)) {
        return prev.filter((id) => id !== questionId);
      } else {
        return [...prev, questionId];
      }
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = testquestionData.map((q) => q._id);
      setSelectedQuestionIds(allIds);
    } else {
      setSelectedQuestionIds([]);
    }
  };

  const clearSelection = () => {
    setSelectedQuestionIds([]);
  };

  // Bulk update handler
  const handleBulkUpdate = async (updateData: {
    level?: string;
    subject?: string;
    standard?: string;
    courseName?: string;
    languageName?: string;
    isLiveMcq?: boolean;
  }) => {
    if (selectedQuestionIds.length === 0) {
      toast.error("No questions selected");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        question_id: selectedQuestionIds,
        ...updateData,
      };

      await ApiService({
        method: "PUT",
        endpoint: apiPaths.bulkUpdateQuestions,
        data: payload,
      });

      toast.success(
        `Successfully updated ${selectedQuestionIds.length} question(s)`
      );

      // Clear selection and refresh data
      setSelectedQuestionIds([]);
      await fetchData(
        paginationInfo.currentPage,
        pageSize,
        searchTerm,
        filters
      );
    } catch (error: any) {
      console.error("Error bulk updating questions:", error);
      const errorMessage =
        error?.response?.data?.message || "Error updating questions";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Cascading filter handlers for bulk edit modal
  const [bulkEditFilteredCourses, setBulkEditFilteredCourses] = useState<
    any[]
  >([]);
  const [bulkEditFilteredSubjects, setBulkEditFilteredSubjects] = useState<
    any[]
  >([]);

  const handleBulkEditStandardChange = (standard: string) => {
    if (standard) {
      const filtered = courses.filter(
        (course) => course.standard === standard
      );
      setBulkEditFilteredCourses(filtered);
    } else {
      setBulkEditFilteredCourses([]);
    }
    setBulkEditFilteredSubjects([]);
  };

  const handleBulkEditCourseChange = (courseName: string) => {
    if (courseName) {
      const filtered = subjectName.filter(
        (subject) => subject.courseName === courseName
      );
      setBulkEditFilteredSubjects(filtered);
    } else {
      setBulkEditFilteredSubjects([]);
    }
  };

  return {
    selectedTestQuestionView,
    selectedTestQuestionId,
    setSelectedTestQuestionId,
    testquestionData,
    loading,
    handleView,
    handleEdit,
    handleDelete,
    handleStatusUp,
    handleDateFilter,
    handleAdd,
    handlePageChange,
    paginationInfo,
    handlePageSizeChange,
    pageSize,
    setPageSize,
    searchTerm,
    setSearchTerm,
    handleSearch,
    handleClearSearch,
    // Filter related exports
    filters,
    setFilters,
    handleFilterChange,
    handleApplyFilters,
    handleClearFilters,
    // Dropdown data
    courses,
    languages,
    subjectName,
    standard,
    users,
    filteredCourses,
    filteredsubjects,
    handleGeneratePdf,
    // Bulk edit functionality
    selectedQuestionIds,
    setSelectedQuestionIds,
    handleSelectQuestion,
    handleSelectAll,
    clearSelection,
    handleBulkUpdate,
    bulkEditFilteredCourses,
    bulkEditFilteredSubjects,
    handleBulkEditStandardChange,
    handleBulkEditCourseChange,
  };
};

export default useTestQuestion;
